import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { toast } from 'sonner';
import { User, Mail, Lock, MapPin, Star, Trash2, Plus, Edit2, ChevronRight, Loader2, Bell } from 'lucide-react';
import PostcodeAddressPicker from '../components/PostcodeAddressPicker';

const AccountPage = () => {
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const [tab, setTab] = useState('profile');
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get('/addresses').then(r => setAddresses(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;
  if (!user) return <Navigate to="/login?redirect=/account" replace />;

  return (
    <div className="max-w-[1000px] mx-auto px-4 py-6">
      <nav className="flex items-center gap-2 text-xs text-neutral-600 mb-4">
        <Link to="/" className="hover:text-[#F46A25]">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[#12263A] font-semibold">My account</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-black text-[#12263A] mb-6">My account</h1>

      <div className="flex border-b border-neutral-200 mb-6">
        <button onClick={() => setTab('profile')} className={`px-5 py-3 text-sm font-bold flex items-center gap-2 ${tab === 'profile' ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-600 hover:text-[#12263A]'}`}>
          <User className="w-4 h-4" /> Profile
        </button>
        <button onClick={() => setTab('addresses')} className={`px-5 py-3 text-sm font-bold flex items-center gap-2 ${tab === 'addresses' ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-600 hover:text-[#12263A]'}`}>
          <MapPin className="w-4 h-4" /> Addresses <span className="text-[10px] bg-neutral-200 text-[#12263A] font-bold px-1.5 py-0.5 rounded">{addresses.length}</span>
        </button>
        <button onClick={() => setTab('newsletter')} data-testid="account-tab-newsletter" className={`px-5 py-3 text-sm font-bold flex items-center gap-2 ${tab === 'newsletter' ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-600 hover:text-[#12263A]'}`}>
          <Bell className="w-4 h-4" /> Newsletter
        </button>
      </div>

      {tab === 'profile' && <ProfileForm user={user} onSaved={refreshAuth} />}
      {tab === 'addresses' && (
        loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25]" /></div>
        : <AddressManager addresses={addresses} setAddresses={setAddresses} />
      )}
      {tab === 'newsletter' && <NewsletterPreferences user={user} onSaved={refreshAuth} />}
    </div>
  );
};

const ProfileForm = ({ user, onSaved }) => {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const patch = {};
    if (name !== user.name) patch.name = name;
    if (email !== user.email) patch.email = email;
    if (newPassword) {
      if (!currentPassword) { toast.error('Enter current password to change it'); return; }
      patch.current_password = currentPassword;
      patch.new_password = newPassword;
    }
    if (Object.keys(patch).length === 0) { toast.info('Nothing to update'); return; }
    setSaving(true);
    try {
      const res = await api.put('/auth/me', patch);
      // If password was changed the server returned a fresh session token —
      // swap it in so this browser stays logged in (other devices are logged out).
      if (res?.data?.token) {
        localStorage.setItem('bq_token', res.data.token);
      }
      toast.success('Profile updated');
      setCurrentPassword(''); setNewPassword('');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-sm p-6 space-y-4 max-w-lg">
      <div>
        <label className="block text-sm font-semibold text-[#12263A] mb-1">Full name</label>
        <div className="relative">
          <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={name} onChange={e => setName(e.target.value)} className="w-full border border-neutral-300 rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-[#F46A25]" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-semibold text-[#12263A] mb-1">Email</label>
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full border border-neutral-300 rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-[#F46A25]" />
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-4">
        <h3 className="font-bold text-sm text-[#12263A] mb-3 flex items-center gap-2"><Lock className="w-4 h-4" /> Change password (optional)</h3>
        <div className="space-y-3">
          <input type="password" placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full border border-neutral-300 rounded-sm py-2.5 px-3 text-sm focus:outline-none focus:border-[#F46A25]" />
          <input type="password" placeholder="New password (min 6)" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full border border-neutral-300 rounded-sm py-2.5 px-3 text-sm focus:outline-none focus:border-[#F46A25]" />
        </div>
      </div>

      <button type="submit" disabled={saving} className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-6 py-2.5 rounded-sm disabled:opacity-60">
        {saving ? 'Saving...' : 'Save changes'}
      </button>
    </form>
  );
};

const AddressManager = ({ addresses, setAddresses }) => {
  const [editing, setEditing] = useState(null); // null | 'new' | address object

  const setFavorite = async (id) => {
    try {
      const r = await api.put(`/addresses/${id}`, { is_favorite: true });
      const list = await api.get('/addresses');
      setAddresses(list.data);
      toast.success('Set as favorite');
    } catch { toast.error('Failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await api.delete(`/addresses/${id}`);
      setAddresses(prev => prev.filter(a => a.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-neutral-600">{addresses.length} saved address{addresses.length !== 1 ? 'es' : ''}</p>
        <button onClick={() => setEditing('new')} className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-4 py-2 rounded-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add address</button>
      </div>
      {addresses.length === 0 && !editing && (
        <div className="bg-white border border-neutral-200 rounded-sm p-8 text-center text-neutral-500">
          No saved addresses yet. Add one so it appears at checkout.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {addresses.map(a => (
          <div key={a.id} className={`bg-white border ${a.is_favorite ? 'border-[#F46A25] ring-1 ring-[#F46A25]' : 'border-neutral-200'} rounded-sm p-4 relative`}>
            {a.is_favorite && (
              <div className="absolute -top-2 left-3 bg-[#F46A25] text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm flex items-center gap-1"><Star className="w-3 h-3 fill-white" /> Favorite</div>
            )}
            <div className="space-y-0.5 text-sm mb-3">
              {a.label && <div className="text-xs uppercase font-bold text-[#F46A25]">{a.label}</div>}
              <div className="font-semibold text-[#12263A]">{a.name}</div>
              <div>{a.line1}</div>
              {a.line2 && <div>{a.line2}</div>}
              <div>{a.city}, {a.postcode}</div>
              {a.phone && <div className="text-neutral-600">☎ {a.phone}</div>}
            </div>
            <div className="flex gap-2 flex-wrap">
              {!a.is_favorite && (
                <button onClick={() => setFavorite(a.id)} className="text-xs border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] font-semibold px-2 py-1 rounded-sm flex items-center gap-1"><Star className="w-3 h-3" /> Set favorite</button>
              )}
              <button onClick={() => setEditing(a)} className="text-xs border border-neutral-300 hover:border-[#F46A25] hover:text-[#F46A25] font-semibold px-2 py-1 rounded-sm flex items-center gap-1"><Edit2 className="w-3 h-3" /> Edit</button>
              <button onClick={() => remove(a.id)} className="text-xs text-red-600 hover:bg-red-50 border border-red-200 font-semibold px-2 py-1 rounded-sm flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <AddressFormDialog
          address={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            api.get('/addresses').then(r => setAddresses(r.data));
          }}
        />
      )}
    </div>
  );
};

const AddressFormDialog = ({ address, onClose, onSaved }) => {
  const isEdit = !!address;
  const [form, setForm] = useState({
    label: address?.label || '',
    name: address?.name || '',
    line1: address?.line1 || '',
    line2: address?.line2 || '',
    city: address?.city || '',
    postcode: address?.postcode || '',
    phone: address?.phone || '',
    is_favorite: address?.is_favorite || false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await api.put(`/addresses/${address.id}`, form);
      } else {
        res = await api.post('/addresses', form);
      }
      toast.success(isEdit ? 'Address updated' : 'Address saved');
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-neutral-200 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-[#12263A]">{isEdit ? 'Edit address' : 'Add address'}</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-[#12263A] text-xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">Label (optional, e.g. Home, Work)</label>
            <input value={form.label} onChange={set('label')} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Full name</label>
            <input required value={form.name} onChange={set('name')} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Address line 1</label>
            <input required value={form.line1} onChange={set('line1')} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Address line 2 (optional)</label>
            <input value={form.line2} onChange={set('line2')} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">City</label>
              <input required value={form.city} onChange={set('city')} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Postcode</label>
              <input required value={form.postcode} onChange={set('postcode')} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">Phone (optional)</label>
            <input value={form.phone} onChange={set('phone')} className="w-full border border-neutral-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_favorite} onChange={set('is_favorite')} className="accent-[#F46A25]" />
            <Star className="w-4 h-4 text-[#F46A25]" /> Set as favorite (shown first at checkout)
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-neutral-300 rounded-sm py-2.5 text-sm font-semibold hover:bg-neutral-100">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-2.5 rounded-sm text-sm disabled:opacity-60">
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Save address')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountPage;

const NewsletterPreferences = ({ user, onSaved }) => {
  const [optIn, setOptIn] = useState(!!user?.newsletter_opt_in);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOptIn(!!user?.newsletter_opt_in); }, [user?.newsletter_opt_in]);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put('/auth/me/newsletter', { opt_in: next });
      setOptIn(next);
      toast.success(next ? 'Subscribed — welcome back!' : 'Unsubscribed from marketing');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-6 max-w-2xl" data-testid="newsletter-preferences">
      <h2 className="text-lg font-bold text-[#12263A] mb-1">Newsletter preferences</h2>
      <p className="text-xs text-neutral-500 mb-5">
        Order confirmations and shipping updates are always sent regardless of this setting.
      </p>

      <label className="flex items-start gap-3 p-4 border border-neutral-200 rounded-sm hover:border-[#F46A25] cursor-pointer" data-testid="newsletter-toggle-row">
        <input
          type="checkbox"
          checked={optIn}
          disabled={saving}
          onChange={e => save(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#F46A25]"
          data-testid="newsletter-toggle-checkbox"
        />
        <div className="flex-1">
          <div className="font-bold text-sm text-[#12263A]">Offers, product updates and Dr Tools trade newsletters</div>
          <div className="text-xs text-neutral-600 mt-1">
            Get bulk-buy deals, new-arrival alerts and pro tips straight to your inbox. You can turn this off at any time.
          </div>
          <div className={`text-xs font-bold mt-2 ${optIn ? 'text-emerald-600' : 'text-neutral-500'}`} data-testid="newsletter-status">
            {saving ? 'Saving…' : optIn ? 'You are subscribed.' : 'You are not subscribed.'}
          </div>
        </div>
      </label>
    </div>
  );
};
